import axios from 'axios';

// Determine the base URL based on environment
const getBaseUrl = () => {
  // In production, use relative URLs which will be served from the same domain
  if (process.env.NODE_ENV === 'production') {
    return '/api';
  }
  
  // For development, you can override with an environment variable or use a default
  return process.env.VUE_APP_API_URL || 'http://localhost:5000/api';
};

const apiClient = axios.create({
  baseURL: getBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000
});

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  response => response,
  error => {
    // Log error for debugging
    console.error('API Error:', error);
    
    // Handle different error types
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const message = error.response.data?.error || error.response.data?.message || 'An error occurred';
      
      // Create a more user-friendly error message
      let userMessage = message;
      if (status === 404) {
        userMessage = 'Resource not found';
      } else if (status === 500) {
        userMessage = 'Server error. Please try again later.';
      } else if (status >= 400 && status < 500) {
        userMessage = message || 'Invalid request';
      }
      
      // Attach user-friendly message to error object
      error.userMessage = userMessage;
    } else if (error.request) {
      // Request was made but no response received
      error.userMessage = 'Network error. Please check your connection.';
    } else {
      // Something else happened
      error.userMessage = 'An unexpected error occurred';
    }
    
    return Promise.reject(error);
  }
);

export default {
  // CP-142: runtime site config ({ affiliate_enabled }).
  getSiteConfig() {
    return apiClient.get('/config', { timeout: 10000 });
  },
  getCruises(params = {}) {
    return apiClient.get('/cruises', { params, timeout: 60000 });
  },
  getCruise(id) {
    return apiClient.get(`/cruises/${id}`);
  },
  getCruisePrices(id, params = {}) {
    return apiClient.get(`/cruises/${id}/prices`, { params });
  },
  getSimilarCruises(cruiseId) {
    return apiClient.get(`/cruises/${cruiseId}/similar`);
  },
  getBestDeals(params = {}, config = {}) {
    return apiClient.get('/cruises/best-deals', { params, ...config });
  },
  getHotDeals(params = {}) {
    return apiClient.get('/cruises/hot-deals', { params });
  },
  getCruiseLineComparison(params = {}) {
    return apiClient.get('/cruises/compare', { params, timeout: 60000 });
  },
  // New filter endpoints (params: cruise_line, destination, departure_port, ship_name for cascading)
  getFilterOptions(params = {}) {
    return apiClient.get('/filters/all', { params, timeout: 60000 });
  },
  getCruiseLines() {
    return apiClient.get('/filters/cruise-lines');
  },
  getDestinations() {
    return apiClient.get('/filters/destinations');
  },
  getDeparturePorts() {
    return apiClient.get('/filters/departure-ports');
  },
  getDurations() {
    return apiClient.get('/filters/durations');
  },
  getShips() {
    return apiClient.get('/filters/ships');
  },
  // Ship endpoints
  getShip(shipName) {
    return apiClient.get(`/ships/${encodeURIComponent(shipName)}`);
  },
  // Image endpoints
  getCruiseImages(cruiseId) {
    return apiClient.get(`/cruises/${cruiseId}/images`);
  },
  // Review endpoints
  getCruiseReviews(cruiseId, params = {}) {
    return apiClient.get(`/cruises/${cruiseId}/reviews`, { params });
  },
  createReview(cruiseId, data) {
    return apiClient.post(`/cruises/${cruiseId}/reviews`, data);
  },
  // Excursion endpoints. Unique content (reviews, viatorUniqueContent) is a
  // separate path that robots.txt disallows.
  getCruiseExcursions(cruiseId, params = {}) {
    return apiClient.get(`/cruises/${cruiseId}/excursions`, { params });
  },
  getViatorUniqueContent(productCode) {
    return apiClient.get(`/viator/products/${encodeURIComponent(productCode)}/unique-content`);
  },
  // Price alert endpoints
  createPriceAlert(data) {
    return apiClient.post('/price-alerts', data);
  },
  cancelPriceAlertByToken(token) {
    return apiClient.post(`/price-alerts/cancel/${encodeURIComponent(token)}`);
  },
  // CP-246 Step 2: proof of ownership is the manage token emailed to the address.
  requestManageLink(email) {
    return apiClient.post('/price-alerts/manage-link', { email });
  },
  getManagedAlerts(manageToken) {
    return apiClient.get('/price-alerts/manage', { headers: { 'X-Manage-Token': manageToken } });
  },
  cancelManagedAlert(alertId, manageToken) {
    return apiClient.post(`/price-alerts/manage/${encodeURIComponent(alertId)}/cancel`, null, {
      headers: { 'X-Manage-Token': manageToken }
    });
  },
  // Home page engagement endpoints
  getHomeDestinations() {
    return apiClient.get('/home/destinations');
  },
  getFeaturedCruises(params = {}) {
    return apiClient.get('/home/featured', { params });
  },
  getHomeStats() {
    return apiClient.get('/home/stats');
  }
};
